import { useContext, useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Button,
  ButtonBase,
  Stack,
  Skeleton,
  Avatar,
  Divider,
  useTheme,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from '@mui/icons-material/Add';
import FolderOffIcon from '@mui/icons-material/FolderOff';
import axios from 'axios';

import { ReportContext, UserContext } from '../Context';
import SettingsFieldDisplay from '../tool/SettingsFieldDisplay';
import { useIndiceInfos } from '../tool/GetIndiceInfos';

const CLOSE_DELAY_MS = 140;
const REFRESH_AFTER_MS = 30_000;
const PANEL_WIDTH = 340;

function formatLastAccessed(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';

  const datePart = date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const heureWord = date.getHours() >= 2 ? 'heures' : 'heure';
  return `le ${datePart} à ${timePart} ${heureWord}`;
}

function HomeQuickAccess({ headerHeight, isDark, onHomeClick, onNavigate }) {
  const { report: currentReport, pickReport } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  const { getIndiceData, getIconComponent } = useIndiceInfos();
  const muiTheme = useTheme();

  const [open, setOpen] = useState(false);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(false);
  const lastFetchRef = useRef(0);
  const closeTimerRef = useRef(null);

  const cubeSize = headerHeight || 56;

  const fetchReports = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const token = sessionStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`/api/user/${user}/accessed-reports`, {
        headers,
      });
      const data = (response.data || [])
        .filter((r) => !r.archived)
        .sort((a, b) => new Date(b.lastAccessed) - new Date(a.lastAccessed));

      // Rafraîchir indice/service et retirer les PV supprimés via l'API métadonnées
      let cleaned = data;
      const items = data.map(({ number, year }) => ({ number, year }));
      if (items.length > 0) {
        try {
          const metaResp = await axios.post(
            '/api/report/metadata',
            { items },
            { headers }
          );
          const metadata = Array.isArray(metaResp.data?.metadata)
            ? metaResp.data.metadata
            : [];
          if (metadata.length > 0) {
            const validKeys = new Set(
              metadata
                .filter((m) => m.found !== false)
                .map((m) => `${m.number}-${m.year}`)
            );
            const metaMap = new Map(
              metadata.map((m) => [`${m.number}-${m.year}`, m])
            );
            cleaned = data
              .filter((r) => validKeys.has(`${r.number}-${r.year}`))
              .map((r) => {
                const meta = metaMap.get(`${r.number}-${r.year}`);
                if (!meta || meta.found === false) return r;
                return {
                  ...r,
                  indice: meta.indice ?? r.indice,
                  unit: meta.unit ?? r.unit,
                  restricted: meta.restricted ?? r.restricted,
                };
              });
          }
        } catch (metaError) {
          console.warn(
            'Impossible de rafraîchir les métadonnées des PV (accès rapide):',
            metaError
          );
        }
      }

      setReports(cleaned);
      lastFetchRef.current = Date.now();
    } catch (err) {
      console.error('Erreur lors du chargement de l\'accès rapide:', err);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
    }, CLOSE_DELAY_MS);
  };

  const handleOpen = () => {
    cancelClose();
    setOpen(true);
    const needsRefresh =
      reports === null || Date.now() - lastFetchRef.current > REFRESH_AFTER_MS;
    if (needsRefresh && !loading) {
      fetchReports();
    }
  };

  useEffect(() => () => cancelClose(), []);

  const navigate = (actionFn) => {
    setOpen(false);
    cancelClose();
    if (typeof onNavigate === 'function') {
      onNavigate(actionFn);
    } else {
      actionFn();
    }
  };

  const handleSelectReport = (report) => {
    navigate(() =>
      pickReport({ number: report.number, year: report.year, new: false })
    );
  };

  const handleNewReport = () => {
    navigate(() => pickReport({ new: true }));
  };

  const handleCubeClick = () => {
    setOpen(false);
    cancelClose();
    if (typeof onHomeClick === 'function') onHomeClick();
  };

  const panelBg = isDark ? 'rgba(21, 34, 46, 0.98)' : 'rgba(255, 255, 255, 0.98)';
  const panelBorder = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)';
  const panelShadow = isDark
    ? '0 14px 40px rgba(0,0,0,0.55)'
    : '0 14px 40px rgba(15, 40, 70, 0.18)';
  const textPrimary = isDark ? '#ecf0f1' : muiTheme.palette.text.primary;
  const textSecondary = isDark ? 'rgba(236,240,241,0.65)' : muiTheme.palette.text.secondary;
  const itemHoverBg = isDark ? 'rgba(79, 195, 247, 0.08)' : 'rgba(25, 118, 210, 0.06)';
  const cubeBg = isDark ? '#4fc3f7' : muiTheme.palette.info.main;
  const cubeBgHover = isDark ? '#29b6f6' : muiTheme.palette.info.dark;

  const renderSkeletons = () => (
    <Stack spacing={1} sx={{ px: 1.5, py: 1 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            py: 0.75,
          }}
        >
          <Skeleton variant="circular" width={28} height={28} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" height={16} />
            <Skeleton variant="text" width="40%" height={12} />
          </Box>
        </Box>
      ))}
    </Stack>
  );

  const renderEmpty = () => (
    <Box
      sx={{
        textAlign: 'center',
        py: 3,
        px: 2,
        color: textSecondary,
      }}
    >
      <FolderOffIcon sx={{ fontSize: 38, opacity: 0.5, mb: 1 }} />
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        Aucun document en cours
      </Typography>
      <Typography variant="caption" sx={{ opacity: 0.8 }}>
        Vos documents consultés apparaîtront ici
      </Typography>
    </Box>
  );

  const visibleReports = (reports || []).filter(
    (r) =>
      !currentReport ||
      r.number !== currentReport.number ||
      r.year !== currentReport.year
  );

  const getBucket = (dateStr) => {
    if (!dateStr) return 'older';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return 'older';
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    if (date.getTime() >= startOfToday.getTime()) return 'today';
    const sevenDaysAgo = new Date(startOfToday);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
    if (date.getTime() >= sevenDaysAgo.getTime()) return 'week';
    return 'older';
  };

  const BUCKETS = [
    { id: 'today', label: "Aujourd'hui" },
    { id: 'week', label: 'Cette semaine' },
    { id: 'older', label: 'Plus ancien' },
  ];

  const groupedReports = BUCKETS.map((bucket) => ({
    ...bucket,
    items: visibleReports.filter((r) => getBucket(r.lastAccessed) === bucket.id),
  })).filter((group) => group.items.length > 0);

  const renderItem = (report) => {
    const indiceData = getIndiceData(report.indice);
    const IconComponent = getIconComponent(indiceData.icon);
    return (
      <ButtonBase
        key={`${report.number}-${report.year}`}
        onClick={() => handleSelectReport(report)}
        focusRipple
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '100%',
          textAlign: 'left',
          gap: 1.25,
          px: 1.25,
          py: 1,
          borderRadius: '10px',
          borderLeft: `3px solid ${indiceData.color}`,
          background: `linear-gradient(90deg, ${indiceData.color}14 0%, rgba(0,0,0,0) 55%)`,
          transition: 'transform 160ms ease, background-color 160ms ease',
          '&:hover': {
            backgroundColor: itemHoverBg,
            transform: 'translateX(2px)',
          },
          '&:focus-visible': {
            outline: `2px solid ${indiceData.color}`,
            outlineOffset: '2px',
          },
        }}
      >
        <Tooltip title={indiceData.name} placement="left" arrow>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: indiceData.color,
              flexShrink: 0,
            }}
          >
            <IconComponent sx={{ fontSize: 18 }} />
          </Avatar>
        </Tooltip>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '0.85rem',
              color: textPrimary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <SettingsFieldDisplay fieldName="shortJudicialDistrict" />.
            {report.indice || '__'}.
            <SettingsFieldDisplay fieldName="judicialDivision" />.
            {report.number}/{report.year}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: textSecondary,
              fontSize: '0.72rem',
              display: 'block',
              lineHeight: 1.3,
            }}
          >
            Dernier accès {formatLastAccessed(report.lastAccessed)}
          </Typography>
        </Box>
      </ButtonBase>
    );
  };

  const renderGroupHeader = (label, count) => (
    <Box
      sx={{
        px: 1.5,
        pt: 1,
        pb: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: panelBg,
        backdropFilter: 'saturate(160%) blur(10px)',
        WebkitBackdropFilter: 'saturate(160%) blur(10px)',
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: textSecondary,
          fontWeight: 700,
          fontSize: '0.68rem',
          letterSpacing: 0.8,
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: '1px',
          backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color: textSecondary,
          fontSize: '0.7rem',
          fontWeight: 600,
          opacity: 0.8,
        }}
      >
        {count}
      </Typography>
    </Box>
  );

  const renderReports = () => (
    <Box sx={{ pb: 0.75 }}>
      {groupedReports.map((group) => (
        <Box key={group.id}>
          {renderGroupHeader(group.label, group.items.length)}
          <Stack spacing={0.5} sx={{ px: 1, pb: 0.5 }}>
            {group.items.map(renderItem)}
          </Stack>
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      {/* Cube maison */}
      <Tooltip title="Retour à l'accueil" placement="right" arrow>
        <Box
          onClick={handleCubeClick}
          onMouseEnter={handleOpen}
          onMouseLeave={scheduleClose}
          onFocus={handleOpen}
          onBlur={scheduleClose}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleCubeClick();
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: headerHeight ? `${headerHeight}px` : '100%',
            width: headerHeight ? `${headerHeight}px` : 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            backgroundColor: cubeBg,
            color: 'white',
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
            outline: 'none',
            zIndex: 1201,
            boxShadow: open
              ? isDark
                ? '0 6px 18px rgba(0,0,0,0.45)'
                : '0 6px 18px rgba(2,136,209,0.35)'
              : 'none',
            '&:hover': {
              backgroundColor: cubeBgHover,
            },
            '&:focus-visible': {
              boxShadow: `0 0 0 3px ${
                isDark ? 'rgba(79,195,247,0.5)' : 'rgba(2,136,209,0.5)'
              }`,
            },
          }}
        >
          <HomeIcon sx={{ fontSize: Math.max(24, cubeSize * 0.55) }} />
        </Box>
      </Tooltip>

      {/* Panneau coulissant */}
      <Box
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        role="menu"
        aria-hidden={!open}
        sx={{
          position: 'absolute',
          top: `${cubeSize}px`,
          left: 0,
          width: `${PANEL_WIDTH}px`,
          maxHeight: 'calc(100vh - 180px)',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: panelBg,
          backdropFilter: 'saturate(160%) blur(10px)',
          WebkitBackdropFilter: 'saturate(160%) blur(10px)',
          border: `1px solid ${panelBorder}`,
          borderTop: 'none',
          borderRadius: '0 12px 12px 0',
          boxShadow: panelShadow,
          opacity: open ? 1 : 0,
          transform: open ? 'translateX(0)' : 'translateX(-10px)',
          pointerEvents: open ? 'auto' : 'none',
          transition:
            'opacity 220ms cubic-bezier(.2,.8,.2,1), transform 220ms cubic-bezier(.2,.8,.2,1)',
          zIndex: 1200,
          overflow: 'hidden',
        }}
      >
        {/* En-tête du panneau */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${panelBorder}`,
            background: isDark
              ? 'linear-gradient(180deg, rgba(79,195,247,0.08), rgba(79,195,247,0))'
              : 'linear-gradient(180deg, rgba(25,118,210,0.06), rgba(25,118,210,0))',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: textPrimary, letterSpacing: 0.2 }}
          >
            Mes documents
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: textSecondary,
              fontWeight: 600,
              px: 0.9,
              py: 0.2,
              borderRadius: '999px',
              backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
            }}
          >
            {loading && reports === null ? '…' : visibleReports.length}
          </Typography>
        </Box>

        {/* Corps */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.18)',
              borderRadius: '3px',
            },
          }}
        >
          {loading && reports === null
            ? renderSkeletons()
            : visibleReports.length > 0
            ? renderReports()
            : renderEmpty()}
        </Box>

        <Divider sx={{ borderColor: panelBorder }} />

        {/* Bouton Nouveau document */}
        <Box sx={{ p: 1.25 }}>
          <Button
            onClick={handleNewReport}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            fullWidth
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              py: 1,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.22)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.32)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Nouveau document
          </Button>
        </Box>
      </Box>
    </>
  );
}

export default HomeQuickAccess;
